import React, { useEffect, useState, useCallback } from 'react';
import { Box } from '@mui/material';

/**
 * Компонент, создающий эффект затемнения всего экрана, кроме выделенного элемента
 *
 * @param {Object} props - свойства компонента
 * @param {HTMLElement} props.targetElement - целевой элемент, который нужно выделить (оставить видимым)
 * @param {number} props.padding - дополнительный отступ вокруг целевого элемента (в пикселях)
 */
const OnboardingOverlay = ({ targetElement, padding = 8 }) => {
  const [position, setPosition] = useState(null);
  // Толщина границы обводки
  const borderWidth = 2;
  // Дополнительное пространство для тени
  const shadowWidth = 4;

  // Функция для проверки, находится ли элемент в видимой области
  const isInViewport = useCallback((element) => {
    if (!element) return false;

    const rect = element.getBoundingClientRect();

    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }, []);

  // Функция для проверки, пересекается ли элемент с видимой областью
  const isElementIntersectingViewport = useCallback((element) => {
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const windowWidth =
      window.innerWidth || document.documentElement.clientWidth;

    // Проверяем, пересекаются ли прямоугольники элемента и видимой области
    return !(
      (
        rect.bottom < 0 || // элемент выше видимой области
        rect.top > windowHeight || // элемент ниже видимой области
        rect.right < 0 || // элемент левее видимой области
        rect.left > windowWidth
      ) // элемент правее видимой области
    );
  }, []);

  // Функция для прокрутки к элементу
  const scrollToElement = useCallback((element) => {
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const elementTop = rect.top + scrollTop;

    // Вычисляем оптимальную позицию скролла
    // (чтобы элемент оказался в центре видимой области)
    const windowHeight = window.innerHeight;
    const elementHeight = rect.height;
    const offset = Math.max(0, (windowHeight - elementHeight) / 2);
    const scrollToPosition = elementTop - offset;

    // Прокручиваем к элементу
    window.scrollTo({
      top: scrollToPosition,
      behavior: 'smooth',
    });
  }, []);

  // Обновляем позицию при изменении размеров окна или целевого элемента
  useEffect(() => {
    if (!targetElement) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const rect = targetElement.getBoundingClientRect();

      // Проверяем видимость элемента
      const isVisible = isInViewport(targetElement);
      const isPartiallyVisible = isElementIntersectingViewport(targetElement);

      // Если элемент полностью за пределами видимой области, прокручиваем к нему
      if (!isPartiallyVisible) {
        scrollToElement(targetElement);
      }

      // Вычисляем позицию с учетом отступов
      // Для вырезаемой области (контент) - это внутренний видимый участок
      const contentTop = Math.max(0, rect.top - padding);
      const contentLeft = Math.max(0, rect.left - padding);
      const contentWidth = rect.width + padding * 2;
      const contentHeight = rect.height + padding * 2;

      // Для обводки (должна быть вокруг вырезаемой области)
      // Обводка должна быть снаружи прозрачной области, поэтому от contentTop/Left отнимаем borderWidth
      const highlightTop = Math.max(0, contentTop - borderWidth);
      const highlightLeft = Math.max(0, contentLeft - borderWidth);
      // К размерам добавляем по borderWidth слева и справа (сверху и снизу)
      const highlightWidth = contentWidth + borderWidth * 2;
      const highlightHeight = contentHeight + borderWidth * 2;

      // Учитываем скролл страницы
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;

      setPosition({
        // Позиция и размеры контента
        top: contentTop,
        left: contentLeft,
        width: contentWidth,
        height: contentHeight,

        // Позиция и размеры обводки
        highlightTop: highlightTop,
        highlightLeft: highlightLeft,
        highlightWidth: highlightWidth,
        highlightHeight: highlightHeight,

        // Размеры окна
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,

        // Скролл страницы
        scrollX: scrollX,
        scrollY: scrollY,

        // Видимость элемента
        isVisible: isVisible,
        isPartiallyVisible: isPartiallyVisible,
      });
    };

    // Обновляем позицию при монтировании
    updatePosition();

    // Добавляем обработчики событий для обновления позиции
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    // Создаем MutationObserver для отслеживания изменений DOM
    const observer = new MutationObserver(updatePosition);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
      observer.disconnect();
    };
  }, [
    targetElement,
    padding,
    borderWidth,
    shadowWidth,
    isInViewport,
    isElementIntersectingViewport,
    scrollToElement,
  ]);

  // Если нет целевого элемента или его позиции, ничего не отображаем
  if (!position) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 9990,
        pointerEvents: 'none',
      }}
    >
      {/* Всегда отображаем затемнение страницы */}
      {/* Маска для всей страницы */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          transition: 'all 0.3s ease',
          pointerEvents: 'auto',
        }}
        onClick={(e) => {
          // Блокируем клики по затемненной области
          e.preventDefault();
          e.stopPropagation();
        }}
      />

      {/* Отображаем вырез и подсветку только если элемент хотя бы частично виден */}
      {position.isPartiallyVisible && (
        <>
          {/* Вырез для целевого элемента (4 прямоугольника, окружающие элемент) */}
          {/* Верхний прямоугольник */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: position.top,
              backgroundColor: 'transparent',
              boxShadow: 'inset 0 0 0 2000px rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(3px)',
              transition: 'all 0.3s ease',
            }}
          />
          {/* Левый прямоугольник */}
          <Box
            sx={{
              position: 'absolute',
              top: position.top,
              left: 0,
              width: position.left,
              height: position.height,
              backgroundColor: 'transparent',
              boxShadow: 'inset 0 0 0 2000px rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(3px)',
              transition: 'all 0.3s ease',
            }}
          />
          {/* Правый прямоугольник */}
          <Box
            sx={{
              position: 'absolute',
              top: position.top,
              left: position.left + position.width,
              width: position.windowWidth - (position.left + position.width),
              height: position.height,
              backgroundColor: 'transparent',
              boxShadow: 'inset 0 0 0 2000px rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(3px)',
              transition: 'all 0.3s ease',
            }}
          />
          {/* Нижний прямоугольник */}
          <Box
            sx={{
              position: 'absolute',
              top: position.top + position.height,
              left: 0,
              width: '100%',
              height: position.windowHeight - (position.top + position.height),
              backgroundColor: 'transparent',
              boxShadow: 'inset 0 0 0 2000px rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(3px)',
              transition: 'all 0.3s ease',
            }}
          />

          {/* Подсветка вокруг целевого элемента */}
          <Box
            sx={{
              position: 'absolute',
              top: position.highlightTop,
              left: position.highlightLeft,
              width: position.highlightWidth,
              height: position.highlightHeight,
              border: `${borderWidth}px solid`,
              borderColor: 'primary.main',
              borderRadius: '8px', // Увеличиваем радиус для лучшего отображения
              // Уменьшаем распространение тени для более четкого эффекта
              boxShadow: `0 0 ${shadowWidth}px ${
                shadowWidth / 2
              }px rgba(79, 195, 247, 0.3)`,
              pointerEvents: 'none',
              zIndex: 9991,
              transition: 'all 0.3s ease',
            }}
          />
        </>
      )}
    </Box>
  );
};

export default OnboardingOverlay;
